import {
  DEFAULT_REPORT_RANGE_MONTHS,
  REPORT_RANGE_LIMIT_KEY,
  REPORT_RANGE_OPTIONS,
  type ReportRangeOption,
} from "src/constants/reportRange";

export type LimitValue = number | string | boolean;

export interface LimitDef {
  key: string;
  label: string;
  /** Defaults to a non-negative integer input; "boolean" renders an on/off checkbox; "select" a dropdown of `options`. */
  type?: "number" | "boolean" | "select";
  /** The choices of a "select" limit. */
  options?: readonly ReportRangeOption[];
  /** What a "select" limit means when the plan does not set it; shown as selected and sent for an active module. */
  defaultValue?: number;
}

export interface ModuleLimitState {
  active: boolean;
  limits: Record<string, LimitValue>;
  includedFeatureCodes: string[];
  hiddenFeatureCodes: string[];
}

export const MODULE_LIMIT_KEYS: Record<string, LimitDef[]> = {
  USER_MODULE: [{ key: "maxUsers", label: "Max Users" }],
  PROJECT_MODULE: [{ key: "maxProjects", label: "Max Projects" }],
  CLIENT_MODULE: [{ key: "maxClients", label: "Max Clients" }],
  EMPLOYEE_MODULE: [{ key: "maxEmployees", label: "Max Employees" }],
  WORKER_MODULE: [{ key: "maxWorkers", label: "Max Workers" }],
  VENDOR_MODULE: [{ key: "maxVendors", label: "Max Vendors" }],
  SUBCONTRACTOR_MODULE: [{ key: "maxSubcontractors", label: "Max Subcontractors" }],
  ACCOUNT_MODULE: [{ key: "maxAccounts", label: "Max Accounts" }],
  REPORT_MODULE: [
    { key: "allowProjectFullReport", label: "Allow Project Full Report", type: "boolean" },
    { key: "allowClientFullReport", label: "Allow Client Full Report", type: "boolean" },
    {
      key: REPORT_RANGE_LIMIT_KEY,
      label: "Report date range",
      type: "select",
      options: REPORT_RANGE_OPTIONS,
      defaultValue: DEFAULT_REPORT_RANGE_MONTHS,
    },
  ],
};

/** Limit keys that hold a true/false permission instead of a number. */
export const BOOLEAN_LIMIT_KEYS: string[] = Object.values(MODULE_LIMIT_KEYS)
  .flat()
  .filter((def) => def.type === "boolean")
  .map((def) => def.key);

export const isBooleanLimit = (key: string): boolean => BOOLEAN_LIMIT_KEYS.includes(key);

const SELECT_LIMIT_DEFS: LimitDef[] = Object.values(MODULE_LIMIT_KEYS)
  .flat()
  .filter((def) => def.type === "select");

/** The definition of a "select" limit (a dropdown of fixed choices), or undefined for any other key. */
export const getSelectLimitDef = (key: string): LimitDef | undefined => SELECT_LIMIT_DEFS.find((def) => def.key === key);

export const isSelectLimit = (key: string): boolean => getSelectLimitDef(key) !== undefined;

const getOptionLabel = (def: LimitDef, value: unknown): string | undefined =>
  def.options?.find((option) => option.value === Number(value))?.label;

/**
 * Builds the `limits` object sent for one plan module. Numeric limits are sent only when filled in.
 * Boolean limits are always sent explicitly for an active module (an unchecked box is `false`), so an
 * edit round-trips exactly; for an inactive module they are sent only if a value was already set.
 * A "select" limit is sent the same way, as its default (for example 1 year) when none was chosen.
 */
export const buildLimitsPayload = (state: ModuleLimitState, limitDefs: LimitDef[]): Record<string, unknown> => {
  const limits: Record<string, unknown> = {};

  if (state.includedFeatureCodes.length > 0) {
    limits.includedFeatureCodes = state.includedFeatureCodes;
  }
  if (state.hiddenFeatureCodes.length > 0) {
    limits.hiddenFeatureCodes = state.hiddenFeatureCodes;
  }

  limitDefs.forEach(({ key, type, defaultValue }) => {
    const value = state.limits[key];
    if (type === "select") {
      const chosen = value !== undefined && value !== "" ? Number(value) : undefined;
      if (state.active || chosen !== undefined) {
        limits[key] = chosen ?? defaultValue;
      }
    } else if (type === "boolean") {
      if (state.active || typeof value === "boolean") {
        limits[key] = value === true;
      }
    } else if (value !== undefined && value !== "") {
      limits[key] = Number(value);
    }
  });

  return limits;
};

/** Short chip text for a module header: `Projects: 5`-style for numbers, the permission name for an enabled boolean. */
export const limitChipLabel = (def: LimitDef, value: LimitValue | undefined): string | null => {
  const { label, type } = def;
  if (type === "select") {
    return value === undefined || value === "" ? null : `${label}: ${getOptionLabel(def, value) ?? value}`;
  }
  if (type === "boolean") {
    return value === true ? label.replace(/^Allow /, "") : null;
  }
  if (value === undefined || value === "") return null;

  return `${label.split(" ").pop()}: ${value}`;
};

/** Value text for the plan details chips: Yes/No for a boolean limit, the raw value otherwise. */
export const formatLimitValue = (key: string, value: unknown): string => {
  const selectDef = getSelectLimitDef(key);
  if (selectDef) {
    return getOptionLabel(selectDef, value) ?? String(value);
  }
  if (isBooleanLimit(key)) {
    return value === true ? "Yes" : "No";
  }

  return String(value);
};
